<template>
  <b-modal
      :visible="visible"
      title="Подбор саундтреков"
      size="xl"
      centered
      hide-footer
      @change="value => $emit('change', value)"
  >
    <div class="mb-1">
      <b-button
          v-for="source in sourceButtons"
          :key="source.value"
          variant="outline-secondary"
          size="sm"
          class="mr-50 mb-50"
          :disabled="isLoading"
          @click="selectSource(source.value)"
      >
        <feather-icon :icon="source.icon" class="mr-50" />
        <span>{{ source.label }}</span>
      </b-button>
    </div>

    <b-overlay :show="isLoading" rounded="sm">
      <!-- Шаг выбора альбома: у Яндекса и MusicBrainz совпадение неточное -->
      <div v-if="albums.length && !items.length" class="mb-1">
        <div class="font-weight-bold mb-50">Выберите альбом:</div>
        <b-list-group>
          <b-list-group-item
              v-for="album in albums"
              :key="album.key"
              button
              @click="loadAlbum(album)"
          >
            {{ album.title }}
            <span v-if="album.subtitle" class="text-muted font-small-2 ml-50">{{ album.subtitle }}</span>
          </b-list-group-item>
        </b-list-group>
      </div>

      <div v-if="loaded && !items.length && !albums.length" class="text-muted">
        Ничего не найдено в этом источнике
      </div>

      <div v-if="items.length">
        <div class="d-flex justify-content-between align-items-center mb-50">
          <div>
            <b-button variant="flat-secondary" size="sm" @click="selectAll">Выбрать все</b-button>
            <b-button variant="flat-secondary" size="sm" @click="clearSelection">Снять выбор</b-button>
          </div>
          <b-button
              variant="primary"
              size="sm"
              :disabled="!selected.length || isAdding"
              @click="addSelected"
          >
            <b-spinner v-if="isAdding" small class="mr-50" />
            <span>Добавить выбранные ({{ selected.length }})</span>
          </b-button>
        </div>

        <b-table
            :items="items"
            :fields="previewColumns"
            small
            striped
            responsive
            class="position-relative"
        >
          <template #cell(select)="data">
            <b-form-checkbox
                :checked="isSelected(data.item)"
                :disabled="data.item.exists"
                @change="toggle(data.item)"
            />
          </template>
          <template #cell(kind)="data">
            <b-badge :variant="data.item.kind === 'licensed' ? 'light-primary' : 'light-secondary'">
              {{ data.item.kind === 'licensed' ? 'В сцене' : 'Альбом' }}
            </b-badge>
          </template>
          <template #cell(title)="data">
            {{ data.item.title }}
            <b-badge v-if="data.item.exists" variant="light-success" class="ml-50">уже есть</b-badge>
          </template>
        </b-table>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BTable, BBadge, BOverlay, BSpinner, BFormCheckbox, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import { ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

const SOURCE_BUTTONS = [
  { value: 'soundtrakd', label: 'Soundtrakd', icon: 'FilmIcon' },
  { value: 'musicbrainz', label: 'MusicBrainz', icon: 'DatabaseIcon' },
  { value: 'yandex', label: 'Яндекс.Музыка', icon: 'MusicIcon' },
]

export default {
  components: {
    BModal, BButton, BTable, BBadge, BOverlay, BSpinner, BFormCheckbox, BListGroup, BListGroupItem,
  },
  props: {
    movieId: { type: [String, Number], required: true },
    visible: { type: Boolean, default: false },
  },
  model: { prop: 'visible', event: 'change' },
  setup(props, { emit }) {
    const toast = useToast()

    const source = ref(null)
    const items = ref([])
    const albums = ref([])
    const selected = ref([])
    const isLoading = ref(false)
    const isAdding = ref(false)
    const loaded = ref(false)

    const previewColumns = [
      { key: 'select', label: '', thStyle: 'width: 40px' },
      { key: 'kind', label: 'Тип', thStyle: 'width: 90px' },
      { key: 'title', label: 'Название', thStyle: 'min-width: 260px' },
      { key: 'artist_name', label: 'Исполнитель', thStyle: 'min-width: 160px' },
    ]

    const notifyError = (error, fallback) => {
      toast({
        component: ToastificationContent,
        props: {
          title: (error.response && error.response.data && error.response.data.message) || fallback,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const reset = () => {
      source.value = null
      items.value = []
      albums.value = []
      selected.value = []
      loaded.value = false
    }

    const loadPreview = queryParams => {
      isLoading.value = true
      store
        .dispatch('movies/previewMovieSoundtracks', { id: props.movieId, queryParams })
        .then(response => {
          items.value = response.data.data
          loaded.value = true
        })
        .catch(error => notifyError(error, 'Не удалось получить список из источника'))
        .finally(() => { isLoading.value = false })
    }

    const loadYandexAlbums = () => {
      isLoading.value = true
      store
        .dispatch('movies/fetchYandexSoundtrackAlbums', { id: props.movieId, query: null })
        .then(response => {
          albums.value = response.data.data.map(album => ({
            key: `ya-${album.id}`,
            id: album.id,
            title: album.title,
            subtitle: [album.artist, album.year].filter(Boolean).join(' · '),
            params: { source: 'yandex', album_id: album.id },
          }))
          loaded.value = true
        })
        .catch(error => notifyError(error, 'Не удалось найти альбомы в Яндекс.Музыке'))
        .finally(() => { isLoading.value = false })
    }

    const loadMusicbrainzGroups = () => {
      isLoading.value = true
      store
        .dispatch('movies/fetchMusicbrainzGroups', { id: props.movieId })
        .then(response => {
          albums.value = response.data.data.map(group => ({
            key: `mb-${group.mbid}`,
            id: group.mbid,
            title: group.title,
            subtitle: group.date,
            params: { source: 'musicbrainz', release_group: group.mbid },
          }))
          loaded.value = true
        })
        .catch(error => notifyError(error, 'Не удалось получить релизы из MusicBrainz'))
        .finally(() => { isLoading.value = false })
    }

    const selectSource = value => {
      source.value = value
      items.value = []
      albums.value = []
      selected.value = []
      loaded.value = false

      if (value === 'soundtrakd') loadPreview({ source: 'soundtrakd' })
      else if (value === 'yandex') loadYandexAlbums()
      else loadMusicbrainzGroups()
    }

    const loadAlbum = album => {
      loadPreview(album.params)
    }

    const isSelected = item => selected.value.includes(item.external_id)

    const toggle = item => {
      if (isSelected(item)) selected.value = selected.value.filter(id => id !== item.external_id)
      else selected.value = [...selected.value, item.external_id]
    }

    const selectAll = () => {
      selected.value = items.value.filter(item => !item.exists).map(item => item.external_id)
    }

    const clearSelection = () => { selected.value = [] }

    const addSelected = () => {
      const payload = items.value.filter(item => selected.value.includes(item.external_id))

      isAdding.value = true
      store
        .dispatch('movies/addMovieSoundtracks', {
          id: props.movieId,
          source: source.value,
          items: payload,
        })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: { title: `Добавлено: ${payload.length}`, icon: 'CheckIcon', variant: 'success' },
          })
          // Бэкенд молча пропускает дубли — помечаем добавленные локально.
          items.value = items.value.map(item => (
            selected.value.includes(item.external_id) ? { ...item, exists: true } : item
          ))
          selected.value = []
          emit('updated')
        })
        .catch(error => notifyError(error, 'Не удалось добавить саундтреки'))
        .finally(() => { isAdding.value = false })
    }

    watch(() => props.visible, value => {
      if (!value) reset()
    })

    return {
      sourceButtons: SOURCE_BUTTONS,
      source,
      items,
      albums,
      selected,
      isLoading,
      isAdding,
      loaded,
      previewColumns,
      selectSource,
      loadAlbum,
      isSelected,
      toggle,
      selectAll,
      clearSelection,
      addSelected,
    }
  },
}
</script>
